import { isConversationListItem } from "@redotech/effect-api-shared/models/shopper-ai/conversation/conversation";
import type { ActionDefinition } from "@redotech/redo-web/actions/action-definition";
import type { ActionId } from "@redotech/redo-web/actions/action-ids";
import {
  conversationActionIds,
  isConversationActionId,
  type ConversationActionId,
} from "@redotech/redo-web/actions/conversation-actions/action-ids";
import { conversationActionDefinitions } from "@redotech/redo-web/actions/conversation-actions/definitions";

// Helper functions for visibility checks - accept unknown for type safety
const isNotDeleted = (context: unknown) =>
  isConversationListItem(context) && context.status !== "DELETED";

const isNotDeletedOrArchived = (context: unknown) =>
  isConversationListItem(context) &&
  context.status !== "DELETED" &&
  context.status !== "ARCHIVED";

/**
 * Full action registry with domain-specific isEnabled/isVisible logic.
 * Merges static definitions from redo-web with context-dependent checks.
 * Uses unknown context type for compatibility with global action scope provider.
 */
export const actionRegistry: Record<
  ConversationActionId,
  ActionDefinition<unknown, ConversationActionId>
> = {
  // Table navigation - no visibility/enabled checks needed
  [conversationActionIds.navigateUp]: {
    ...conversationActionDefinitions.navigateUp,
  },
  [conversationActionIds.navigateDown]: {
    ...conversationActionDefinitions.navigateDown,
  },
  [conversationActionIds.openConversation]: {
    ...conversationActionDefinitions.openConversation,
  },
  [conversationActionIds.openActionMenu]: {
    ...conversationActionDefinitions.openActionMenu,
  },

  // Direct conversation actions with context-specific checks
  [conversationActionIds.close]: {
    ...conversationActionDefinitions.close,
    isEnabled: (context) =>
      isConversationListItem(context) && context.status !== "CLOSED",
    isVisible: isNotDeletedOrArchived,
  },
  [conversationActionIds.reopen]: {
    ...conversationActionDefinitions.reopen,
    isEnabled: (context) =>
      isConversationListItem(context) &&
      (context.status === "CLOSED" || context.status === "SNOOZED"),
    isVisible: isNotDeletedOrArchived,
  },
  [conversationActionIds.snooze]: {
    ...conversationActionDefinitions.snooze,
    isEnabled: (context) =>
      isConversationListItem(context) &&
      context.status !== "SNOOZED" &&
      context.status !== "CLOSED",
    isVisible: isNotDeletedOrArchived,
  },
  [conversationActionIds.archive]: {
    ...conversationActionDefinitions.archive,
    isEnabled: (context) =>
      isConversationListItem(context) && context.status !== "ARCHIVED",
    isVisible: isNotDeleted,
  },
  [conversationActionIds.delete]: {
    ...conversationActionDefinitions.delete,
    isEnabled: (context) =>
      isConversationListItem(context) && context.status !== "DELETED",
  },
  [conversationActionIds.markRead]: {
    ...conversationActionDefinitions.markRead,
    isEnabled: (context) =>
      isConversationListItem(context) && context.readAt === null,
  },
  [conversationActionIds.markUnread]: {
    ...conversationActionDefinitions.markUnread,
    isEnabled: (context) =>
      isConversationListItem(context) && context.readAt !== null,
  },

  // Submenu trigger actions
  [conversationActionIds.setPriority]: {
    ...conversationActionDefinitions.setPriority,
    isVisible: isNotDeletedOrArchived,
  },
  [conversationActionIds.assign]: {
    ...conversationActionDefinitions.assign,
    isVisible: isNotDeletedOrArchived,
  },
  [conversationActionIds.addTag]: {
    ...conversationActionDefinitions.addTag,
    isVisible: isNotDeleted,
  },

  // Priority options
  [conversationActionIds.priorityUrgent]: {
    ...conversationActionDefinitions.priorityUrgent,
    isVisible: isNotDeletedOrArchived,
  },
  [conversationActionIds.priorityClear]: {
    ...conversationActionDefinitions.priorityClear,
    isVisible: isNotDeletedOrArchived,
  },

  // Snooze options
  [conversationActionIds.snooze1h]: {
    ...conversationActionDefinitions.snooze1h,
    isVisible: isNotDeletedOrArchived,
  },
  [conversationActionIds.snooze4h]: {
    ...conversationActionDefinitions.snooze4h,
    isVisible: isNotDeletedOrArchived,
  },
  [conversationActionIds.snooze24h]: {
    ...conversationActionDefinitions.snooze24h,
    isVisible: isNotDeletedOrArchived,
  },
  [conversationActionIds.snoozeCustom]: {
    ...conversationActionDefinitions.snoozeCustom,
    isVisible: isNotDeletedOrArchived,
  },
  [conversationActionIds.snoozeClear]: {
    ...conversationActionDefinitions.snoozeClear,
    isVisible: isNotDeletedOrArchived,
  },

  // Message input actions - context will be provided by message input component
  [conversationActionIds.sendMessage]: {
    ...conversationActionDefinitions.sendMessage,
  },
  [conversationActionIds.deleteDraft]: {
    ...conversationActionDefinitions.deleteDraft,
  },

  // Reply mode actions
  [conversationActionIds.reply]: { ...conversationActionDefinitions.reply },
  [conversationActionIds.replyAll]: {
    ...conversationActionDefinitions.replyAll,
  },
  [conversationActionIds.forward]: { ...conversationActionDefinitions.forward },
};

export function getAction(id: ActionId): ActionDefinition<unknown, ActionId> {
  if (!isConversationActionId(id)) {
    throw new Error(`Unknown action: ${id}`);
  }
  return actionRegistry[id];
}
